<template>
  <div class="modal fade" id="highlight-detail-dialog" tabindex="-1" aria-labelledby="highlightDetailLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content detail-sheet">
        <DetailSheetHeader
          :title="title"
          :subtitle="subtitle"
          :icon-url="iconUrl"
          :icon-alt="prettyTypeName"
        />

        <!-- `p-3` (a !important utility) rather than a scoped rule, so the padding
             wins against Bootstrap's own .modal-body regardless of CSS order. -->
        <div class="modal-body p-3">
          <DetailSheetSection v-if="photos.length" plain>
            <DetailSheetPhotoStrip :photos="photos" @open="openLightbox" />
          </DetailSheetSection>

          <DetailSheetSection v-if="highlight?.description" plain>
            <p class="highlight-description">{{ displayedDescription }}</p>
            <button
              v-if="descriptionNeedsTruncation"
              type="button"
              class="btn btn-link btn-sm p-0 see-more-toggle"
              @click="descriptionExpanded = !descriptionExpanded"
            >
              {{ descriptionExpanded ? $t('highlight_detail_dialog.see_less') : $t('highlight_detail_dialog.see_more') }}
            </button>
          </DetailSheetSection>

          <DetailSheetSection v-if="audioTracks.length" plain>
            <div v-for="track in audioTracks" :key="track.key" class="audio-item">
              <p class="audio-item__title">
                <span class="material-icons">volume_up</span>{{ track.title || $t('highlight_detail_dialog.audio') }}
              </p>
              <audio :src="track.url" controls preload="none" class="w-100"></audio>
            </div>
          </DetailSheetSection>

          <DetailSheetSection v-if="hasActions">
            <DetailSheetActionRow
              v-if="placeLink"
              :label="$t('highlight_detail_dialog.place_details')"
              icon="info"
              @click="showPlaceDetails"
            />
            <DetailSheetActionRow
              v-if="canEdit"
              :label="$t('shared.edit_details')"
              icon="edit"
              @click="editHighlight"
            />
            <DetailSheetActionRow
              v-if="canIncludeOnRoute"
              :label="isActiveRouteEmpty ? $t('highlight_detail_dialog.set_as_start_point') : $t('highlight_detail_dialog.include_on_route')"
              :icon="isActiveRouteEmpty ? 'flag' : 'add_location_alt'"
              @click="includeOnRoute"
            />
            <DetailSheetActionRow
              v-if="showSetStageEnd"
              :label="$t('highlight_detail_dialog.set_as_stage_end')"
              icon="outlined_flag"
              @click="setAsStageEnd"
            />
            <DetailSheetActionRow
              v-if="hasLocation"
              :label="$t('highlight_detail_dialog.plan_a_hike_from_here')"
              icon="explore"
              @click="planHikeFromHighlight"
            />
            <DetailSheetActionRow
              v-if="hasLocation"
              :label="$t('highlight_detail_dialog.driving_directions')"
              icon="directions_car"
              @click="getDirections"
            />
            <DetailSheetActionRow
              v-if="hasLocation"
              :label="$t('highlight_detail_dialog.view_on_google_street_view')"
              icon="streetview"
              pro
              @click="openStreetView"
            />
            <DetailSheetActionRow
              v-if="shareUrl"
              :label="$t('highlight_detail_dialog.share_highlight')"
              icon="share"
              :hint="shareUrl"
              :status="showCopiedShare ? $t('common.copied') : ''"
              @click="shareHighlight"
            />
            <DetailSheetActionRow
              v-if="canDelete"
              :label="$t('highlight_detail_dialog.delete_highlight')"
              icon="delete"
              danger
              @click="deleteHighlight"
            />
          </DetailSheetSection>

          <DetailSheetSection v-if="hasDetails">
            <DetailSheetMetaRow
              v-if="formattedCoordinates"
              :label="$t('shared.coords')"
              :value="formattedCoordinates"
              copyable
              :copied="showCopiedCoords"
              @copy="copyCoordinates"
            />
            <DetailSheetMetaRow v-if="prettyTypeName" :label="$t('shared.type_2')" :value="prettyTypeName" />
            <DetailSheetMetaRow v-if="highlight?.dayNumber" :label="$t('highlight_detail_dialog.day')" :value="$t('highlight_detail_dialog.day_number', { number: highlight.dayNumber })" />
            <DetailSheetMetaRow v-if="highlight?.id" label="ID" :value="String(highlight.id)" />
          </DetailSheetSection>
        </div>

        <div class="detail-sheet__footer">
          <button type="button" class="btn detail-sheet__close" data-bs-dismiss="modal">{{ $t('shared.close') }}</button>
        </div>
      </div>
    </div>

    <DetailSheetLightbox
      :open="lightboxOpen"
      :photos="photos"
      :index="lightboxIndex"
      @close="lightboxOpen = false"
      @prev="showPrevPhoto"
      @next="showNextPhoto"
    />
  </div>
</template>

<script lang="ts">
import { Modal } from 'bootstrap';
import { computed, defineComponent, onBeforeUnmount, ref } from 'vue';
import { useStore } from 'vuex';
import DetailSheetActionRow from '../../utils/shared_components/detail_sheet/DetailSheetActionRow.vue';
import DetailSheetHeader from '../../utils/shared_components/detail_sheet/DetailSheetHeader.vue';
import DetailSheetLightbox from '../../utils/shared_components/detail_sheet/DetailSheetLightbox.vue';
import DetailSheetMetaRow from '../../utils/shared_components/detail_sheet/DetailSheetMetaRow.vue';
import DetailSheetPhotoStrip from '../../utils/shared_components/detail_sheet/DetailSheetPhotoStrip.vue';
import DetailSheetSection from '../../utils/shared_components/detail_sheet/DetailSheetSection.vue';
import { detailSheetSubtitle } from '../../utils/helpers/detailSheetFormat';
import { GridRefUtils } from '../../utils/helpers/GridRefPreference';
import { formatGoToLocationLatLon } from '../../utils/helpers/goToLocationLatLon';
import {
  highlightAudioTracks,
  highlightLatLng,
  highlightPhotos,
  highlightShareUrl,
  highlightStageDestination,
} from '../../utils/helpers/highlightDetails';
import { highlightPoiLink } from '../../utils/helpers/PoiToHikeHighlightHelper';
import { activeStageEndDay } from '../plan_trail/services/stage-destinations';
import { googleStreetViewUrl, hikePlannerStartUrl, openGoogleMaps } from '../../utils/helpers/OpenOnGoogleMaps';
import { clampNextIndex, clampPrevIndex } from '../../utils/helpers/photoViewerNav';
import { POIUtility } from '../../utils/models/POI';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import { truncateAtWord, needsTruncation } from '../../utils/helpers/truncateText';
import { i18n } from '../../i18n';

// The planner publishes these on `window` when it is the host app; they are
// read through a cast so this dialog carries no compile-time dependency on it.
const planner = () => window as any;

// Highlight descriptions can run to several paragraphs (e.g. the imported
// Causeway Coast Way guide text). Collapse anything longer than this behind a
// "See more" toggle so the dialog stays readable.
const DESCRIPTION_TRUNCATE_LIMIT = 300;

const COPIED_FEEDBACK_MS = 2000;

export default defineComponent({
  name: 'HighlightDetailDialog',
  components: {
    DetailSheetActionRow,
    DetailSheetHeader,
    DetailSheetLightbox,
    DetailSheetMetaRow,
    DetailSheetPhotoStrip,
    DetailSheetSection,
  },
  setup() {
    // Undefined when the host app mounts this dialog without a Vuex store; every
    // read below is optional so the component stays usable in that case.
    const store = useStore();
    const highlight = ref<any>(null);
    const descriptionExpanded = ref(false);
    const lightboxOpen = ref(false);
    const lightboxIndex = ref(0);
    const showCopiedShare = ref(false);
    const showCopiedCoords = ref(false);

    const title = computed(() => highlight.value?.title || i18n.global.t('highlight_detail_dialog.highlight'));

    const prettyTypeName = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        return POIUtility.fromString(highlight.value.type).prettyName();
      } catch (e) {
        return highlight.value.type;
      }
    });

    const iconUrl = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        return POIUtility.fromString(highlight.value.type).getPublicImageIcon();
      } catch (e) {
        return POIUtility.fromString('other').getPublicImageIcon();
      }
    });

    const subtitle = computed(() => detailSheetSubtitle([
      prettyTypeName.value,
      highlight.value?.dayNumber ? i18n.global.t('highlight_detail_dialog.day_number', { number: highlight.value.dayNumber }) : '',
    ]));

    const descriptionNeedsTruncation = computed(() => needsTruncation(
      highlight.value?.description,
      DESCRIPTION_TRUNCATE_LIMIT,
    ));

    const displayedDescription = computed(() => {
      const description = highlight.value?.description || '';
      return descriptionExpanded.value
        ? description
        : truncateAtWord(description, DESCRIPTION_TRUNCATE_LIMIT);
    });

    const photos = computed(() => highlightPhotos(highlight.value));
    const audioTracks = computed(() => highlightAudioTracks(highlight.value));
    // Retained alongside `audioTracks` because callers/specs read it directly.
    const hasAudio = computed(() => audioTracks.value.length > 0);

    const latLng = computed(() => highlightLatLng(highlight.value));
    const hasLocation = computed(() => latLng.value !== null);

    const formattedCoordinates = computed(() => {
      if (!latLng.value) return '';
      try {
        return String(GridRefUtils.preference().convert(latLng.value as any));
      } catch (e) {
        return formatGoToLocationLatLon(latLng.value.lat, latLng.value.lng);
      }
    });

    const shareUrl = computed(() => (typeof window === 'undefined'
      ? null
      : highlightShareUrl(highlight.value, window.location.href)));

    // The planner is the only host that can mutate a highlight or the route, so
    // the editing actions key off its store module being present.
    const inPlanner = computed(() => store?.state?.planTrail !== undefined);
    const canIncludeOnRoute = computed(() => inPlanner.value && hasLocation.value);

    const activeRoute = () => {
      const planTrail = store?.state?.planTrail;
      return planTrail?.activeRouteKey ? planTrail.routes?.[planTrail.activeRouteKey] : null;
    };

    // With no markers on the active route (or nothing active), "include on route"
    // places the route's first marker, so it reads as "set as start point".
    const isActiveRouteEmpty = computed(() => {
      if (!inPlanner.value) return false;
      const route = activeRoute();
      return !route || route.markers.length === 0;
    });

    // The selected route's day, when this can end it (see activeStageEndDay).
    const stageEndDay = computed(() => {
      const planTrail = store?.state?.planTrail;
      return planTrail ? activeStageEndDay(planTrail.routes || {}, planTrail.activeRouteKey) : null;
    });

    const showSetStageEnd = computed(() => inPlanner.value && hasLocation.value && stageEndDay.value != null);
    const canDelete = computed(() => inPlanner.value && !!highlight.value?.clientId);
    // `window.openHighlightEditDialog` is not reactive, and the planner only
    // publishes it from its own onMounted, which runs after this child dialog has
    // already rendered once. A computed that read it directly cached `false` for
    // the life of the dialog and the Edit action never appeared, so the check is
    // re-taken each time a highlight is shown instead.
    const editorAvailable = ref(false);
    const canEdit = computed(() => inPlanner.value && editorAvailable.value);

    // The place it was made from (see highlightPoiLink), when this page can
    // show places — the full sheet, with contact details, opening hours etc.
    // Re-checked as each highlight is shown, like editorAvailable.
    const placeSheetAvailable = ref(false);
    const placeLink = computed(() => {
      const link = highlightPoiLink(highlight.value?.poiType, highlight.value?.poiId);
      return link && placeSheetAvailable.value ? link : null;
    });

    // Don't render a titled panel with nothing in it.
    const hasActions = computed(() => hasLocation.value || !!shareUrl.value || canEdit.value || canDelete.value || !!placeLink.value);

    const stageDestination = () => highlightStageDestination(
      highlight.value,
      i18n.global.t('highlight_detail_dialog.highlight'),
    );
    const hasDetails = computed(() => !!formattedCoordinates.value
      || !!prettyTypeName.value
      || !!highlight.value?.dayNumber
      || !!highlight.value?.id);

    const modalElement = () => document.getElementById('highlight-detail-dialog');

    const hideDialog = () => {
      const element = modalElement();
      if (!element) return null;
      const modal = Modal.getInstance(element);
      modal?.hide();
      return element;
    };

    // Bootstrap leaves a backdrop behind when a second modal is opened over one
    // that is already showing; trim any that no longer belong to a visible modal.
    const cleanupOrphanedBackdrops = () => {
      const openModals = document.querySelectorAll('.modal.show');
      const backdrops = document.querySelectorAll('.modal-backdrop');

      backdrops.forEach((backdrop, index) => {
        if (index >= openModals.length) backdrop.remove();
      });

      if (openModals.length === 0 && backdrops.length > 0) {
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
      }
    };

    const openLightbox = (index: number) => {
      lightboxIndex.value = index;
      lightboxOpen.value = true;
    };

    const showPrevPhoto = () => { lightboxIndex.value = clampPrevIndex(lightboxIndex.value); };
    const showNextPhoto = () => { lightboxIndex.value = clampNextIndex(lightboxIndex.value, photos.value.length); };

    const flashCopied = (flag: typeof showCopiedShare) => {
      flag.value = true;
      window.setTimeout(() => { flag.value = false; }, COPIED_FEEDBACK_MS);
    };

    const copyText = async (text: string, flag: typeof showCopiedShare) => {
      try {
        await navigator.clipboard.writeText(text);
        flashCopied(flag);
      } catch (err) {
        console.error('Unable to copy text to clipboard', err);
      }
    };

    const shareHighlight = () => {
      if (shareUrl.value) copyText(shareUrl.value, showCopiedShare);
    };

    const copyCoordinates = () => {
      if (formattedCoordinates.value) copyText(formattedCoordinates.value, showCopiedCoords);
    };

    const getDirections = () => {
      if (latLng.value) openGoogleMaps(latLng.value.lat, latLng.value.lng);
    };

    const planHikeFromHighlight = () => {
      if (!latLng.value) return;
      window.open(hikePlannerStartUrl(window.location.origin, latLng.value.lat, latLng.value.lng), '_blank');
    };

    const openStreetView = async () => {
      if (!latLng.value) return;
      const isPro = await RevenueCatWrapper.isProOrProPlus();
      if (!isPro) {
        window.location.href = '/purchases';
        return;
      }
      window.open(googleStreetViewUrl(latLng.value.lat, latLng.value.lng), '_blank');
    };

    const includeOnRoute = async () => {
      if (!latLng.value) return;

      if (typeof planner().canEditReference === 'function' && !planner().canEditReference()) {
        if (typeof planner().checkReferenceEditWarning !== 'function') return;
        const confirmed = await planner().checkReferenceEditWarning();
        if (!confirmed) return;
      }

      // Captured before the marker lands, which is what makes the route non-empty.
      const wasStartPoint = isActiveRouteEmpty.value;
      const dayNumber = activeRoute() ? (activeRoute().day ?? 1) : null;
      const destination = stageDestination();

      hideDialog();
      await store?.dispatch('planTrail/addMarkerAtCoordinates', {
        lng: latLng.value.lng,
        lat: latLng.value.lat,
      });

      // Record the attachment too, so the highlight's icon renders in place of the plain pin.
      if (wasStartPoint && dayNumber != null && destination) {
        await store?.dispatch('planTrail/attachTrailStart', { dayNumber, destination });
      }
    };

    const attachToDay = async (dayNumber: number) => {
      const destination = stageDestination();
      if (!destination) return;
      hideDialog();
      await store?.dispatch('planTrail/attachStageDestination', { dayNumber, destination });
    };

    // Ends the selected route's day here.
    const setAsStageEnd = () => {
      if (stageEndDay.value != null) attachToDay(stageEndDay.value);
    };

    const showPlaceDetails = () => {
      const link = placeLink.value;
      if (!link) return;
      const seed = {
        id: link.poiId,
        type: highlight.value?.type,
        name: highlight.value?.title || null,
        lonlat: latLng.value ? [latLng.value.lng, latLng.value.lat] : null,
      };
      const element = modalElement();
      // Opened once this sheet has gone, so the two don't share a backdrop.
      if (element) {
        element.addEventListener('hidden.bs.modal', () => planner().showPoi?.(link.poiType, link.poiId, seed), { once: true });
        hideDialog();
        return;
      }
      planner().showPoi?.(link.poiType, link.poiId, seed);
    };

    const editHighlight = () => {
      const highlightCopy = highlight.value ? JSON.parse(JSON.stringify(highlight.value)) : null;
      const element = modalElement();

      if (element && highlightCopy) {
        // Wait for the dismiss animation so the editor doesn't fight this
        // dialog's backdrop teardown.
        element.addEventListener('hidden.bs.modal', () => {
          planner().openHighlightEditDialog?.(highlightCopy);
        }, { once: true });
        hideDialog();
        return;
      }

      if (highlightCopy) planner().openHighlightEditDialog?.(highlightCopy);
    };

    const deleteHighlight = async () => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(i18n.global.t('highlight_detail_dialog.delete_confirm'))) return;

      await store?.dispatch('planTrail/deleteHikeHighlight', highlight.value.clientId);
      hideDialog();
    };

    const showHighlight = (highlightData: any) => {
      // The map click payload only carries the feature's GeoJSON properties, so
      // merge in the stored highlight (images, media) when we can find it.
      let resolved = highlightData;
      const stored = highlightData?.clientId
        ? store?.state?.planTrail?.hikeHighlights?.find((item: any) => item.clientId === highlightData.clientId)
        : null;

      if (stored) {
        resolved = { ...JSON.parse(JSON.stringify(stored)), ...highlightData };
      }

      cleanupOrphanedBackdrops();

      editorAvailable.value = typeof planner().openHighlightEditDialog === 'function';
      placeSheetAvailable.value = typeof planner().showPoi === 'function';
      highlight.value = resolved;
      descriptionExpanded.value = false;
      lightboxOpen.value = false;
      lightboxIndex.value = 0;

      const element = modalElement();
      if (!element) return;

      const modal = Modal.getInstance(element) ?? new Modal(element);
      element.addEventListener('hidden.bs.modal', cleanupOrphanedBackdrops, { once: true });
      modal.show();
    };

    window.showHighlightDetail = showHighlight;

    onBeforeUnmount(() => {
      if (window.showHighlightDetail === showHighlight) delete window.showHighlightDetail;
      cleanupOrphanedBackdrops();
    });

    return {
      attachToDay,
      audioTracks,
      canDelete,
      canEdit,
      canIncludeOnRoute,
      copyCoordinates,
      deleteHighlight,
      descriptionExpanded,
      descriptionNeedsTruncation,
      displayedDescription,
      editHighlight,
      formattedCoordinates,
      getDirections,
      hasActions,
      hasAudio,
      hasDetails,
      hasLocation,
      highlight,
      iconUrl,
      includeOnRoute,
      isActiveRouteEmpty,
      lightboxIndex,
      lightboxOpen,
      openLightbox,
      openStreetView,
      photos,
      placeLink,
      planHikeFromHighlight,
      prettyTypeName,
      setAsStageEnd,
      shareHighlight,
      shareUrl,
      showNextPhoto,
      showPrevPhoto,
      showSetStageEnd,
      showCopiedCoords,
      showCopiedShare,
      showPlaceDetails,
      subtitle,
      title,
    };
  },
});
</script>

<style scoped>
.detail-sheet {
  /* Let the header's background follow the dialog's rounded corners. */
  overflow: hidden;
}

/* Preserve the paragraph breaks (\n) stored in the description instead of
   collapsing them the way default HTML white-space handling would. */
.highlight-description {
  margin: 0;
  white-space: pre-line;
  color: #fff;
  font-size: 0.9375rem;
  line-height: 1.55;
}

.see-more-toggle {
  color: var(--yellow);
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
}

.see-more-toggle:hover {
  color: var(--yellow);
  text-decoration: underline;
}

.audio-item + .audio-item {
  margin-top: 0.75rem;
}

.audio-item__title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0 0 0.25rem;
  color: var(--text-muted);
  font-size: 0.8125rem;
}

.audio-item__title .material-icons {
  font-size: 17px;
}

.detail-sheet__footer {
  padding: 0 1rem 1rem;
}

.detail-sheet__close {
  width: 100%;
  background: var(--gray);
  border: 1px solid var(--gray-border);
  color: var(--text-muted);
  font-weight: 600;
}

.detail-sheet__close:hover {
  background: var(--highlight);
  color: #fff;
}
</style>
